import React from "react";
import { createRoot } from 'react-dom/client'
import { ApolloClient, ApolloProvider, ApolloLink, createHttpLink, InMemoryCache, from, Observable } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context"; // Use dedicated setContext import

import "./css/styles.css";
import "../node_modules/bootstrap/dist/css/bootstrap.css";
import { appConfig } from "./configuration/AppConfig";

import App from "./App";
import { HoverProvider } from "./context/HoverContext";
import reportWebVitals from "./reportWebVitals";
import { UtahDesignSystemContextProvider } from "./components/archive/utah-design-system/react/contexts/UtahDesignSystemContext/UtahDesignSystemContextProvider";

// --- CONFIGURATION ---
let lslLink = createHttpLink({ uri: `${appConfig.lslLink}` });

let gqlLink = createHttpLink({ 
  uri: `${appConfig.gqlLink}`,
  credentials: 'include', // This is mandatory for sending cookies
});

const REFRESH_ENDPOINT = `${appConfig.authEndpoint}/refresh${appConfig.authParams}`;
const ACCESS_TOKEN_KEY = "token"; 

const container = document.getElementById('root');
const root = createRoot(container);

const destroyAuthCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

// --- UTILITY FUNCTIONS ---
const handleLogoutRedirect = () => {
  console.log("Logging out and redirecting...");
  destroyAuthCookie("v2Authenticated");
  window.location.assign(`${appConfig.revoke}${appConfig.authParams}&jwt=${sessionStorage.getItem(ACCESS_TOKEN_KEY)}`);

  sessionStorage.removeItem("user");
  sessionStorage.removeItem("auth");
  sessionStorage.removeItem("route");
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  window.location.assign("/");
};

// 1. Create a variable outside the link to hold the 'in-flight' promise
let refreshTokenPromise = null;

const refreshAccessToken = async () => {
  // 2. If a refresh is already happening, return the existing promise
  if (refreshTokenPromise) {
    console.log("Refresh already in progress, awaiting existing promise...");
    return refreshTokenPromise;
  }

  // 3. Create the promise and assign it to the variable
  refreshTokenPromise = (async () => {
    try {
      console.log("Attempting to refresh access token...");
      const response = await fetch(REFRESH_ENDPOINT, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
        return data.access_token;
      }
      throw new Error("Refresh failed");
    } catch (error) {
      handleLogoutRedirect();
      return null;
    } finally {
      // 4. Clear the promise when done so future failures can refresh again
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
};

const authLink = setContext((_, { headers }) => {
  const token = sessionStorage.getItem(ACCESS_TOKEN_KEY);
  return {
    headers: {
      ...headers,
      "api-key": "873b8cu3nev6r2b9nm98vd56scd20xbdygd",
      ...(token && token !== "null" && { authorization: `Bearer ${token}` }),
    }
  };
});

// Helper to read a specific cookie by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

let isRefreshing = false;

const combinedErrorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    const expiredError = graphQLErrors.find(
      err => err.extensions?.code === 'UNAUTHENTICATED' && err.extensions?.subCode === 'ACCESS_TOKEN_EXPIRED'
    );

    if (expiredError) {
      if (isRefreshing) return; // Prevent loops if multiple queries fail at once

      return new Observable(observer => {
        (async () => {
          isRefreshing = true;
          console.log('Missing/Expired token detected via GraphQL error. Attempting refresh...');
          
          const newAccessToken = await refreshAccessToken();
          if (newAccessToken) {
            operation.setContext(({ headers }) => ({
              headers: { ...headers, authorization: `Bearer ${newAccessToken}` },
            }));
            forward(operation).subscribe(observer);
          } else {
            observer.error(expiredError);
          }
          
          isRefreshing = false;
        })();
      });
    }
  }

  if (networkError) {
    console.error(`[Network Error]:`, networkError);
    if ('statusCode' in networkError && (networkError.statusCode === 401 || networkError.statusCode === 403)) {
      if (!getCookie('refresh_token')) {
        handleLogoutRedirect();
      }
    }
  }
});

const customLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext();
  const contextId = context.apiId; 

  let selectedLink;
  if (contextId === "lsl") {
    selectedLink = lslLink;
  } else {
    selectedLink = gqlLink;
  }

  return selectedLink.request(operation);
});

const link = from([
  combinedErrorLink, 
  authLink, 
  customLink
]);

const client = new ApolloClient({
  connectToDevTools: true,
  cache: new InMemoryCache(),
  link: link
});

root.render(
 <ApolloProvider client={client}>
   <HoverProvider>
     <UtahDesignSystemContextProvider>
       <App />
     </UtahDesignSystemContextProvider>
   </HoverProvider>
  </ApolloProvider>
);

reportWebVitals();